// hoy86 · ADENDA z62-2 + z64-2 (Diego, 29→30-oct, MANDA): «Sugerir con IA» se queda con lo que MENOS se equivoca con recibos reales,
// y «si la IA de verdad acierta más que la curva, SE PRENDE aunque cada uso cueste centavos de dólar (si falla, cae a la curva con
// aviso)». Medido el 30-oct con recibos reales completos de CFE, la IA llamada COMO CORRE AQUÍ (un cliente por llamada, el prompt de
// paquetes/ia/src/sugerir.ts), dos corridas (Desktop/Solarix/Cotizador/investigacion/SUGERIR-IA-MEDICION-2026-10-30.md):
//   · PDBT (el único negocio donde se ofrece el botón: su historial trae fechas): la IA SÍ acierta más. Error del total del año
//     contra la curva de producción: −6.4 puntos en promedio [IC 95 % −12.2, −1.5] y −6.2 [−11.8, −1.3] en la segunda corrida; gana
//     en 31 de 40 casos las dos veces. → se PRENDE.
//   · Casas (1E y 1F de Sonora): NO acierta más (+1.0 puntos [−0.9, +2.8] en 190 casos; en 1F −0.6 [−3.6, +2.4]; por mes la curva
//     es mejor: mediana 20 % contra 25 %). → se queda la curva de Sonora, sin costo, sin esperar y sin nada que pueda fallar.
//   · Lo demás (GDBT, un negocio con recibo de casa, GDMTO/GDMTH con fechas): no se midió → la curva (lo de siempre).
import type { PlanCompletarAnio } from './completar-anio.ts';
import { porEstimarDelPlan } from './completar-anio.ts';
import type { PeriodoPorEstimar } from './completar-anio.ts';
import type { Tarifa } from './dominio/basicos.ts';
import type { EntradaSugerirConsumos } from './dominio/ia.ts';
import { numero } from './dinero.ts';

/** Las tarifas donde «Sugerir con IA» le pregunta a la IA (en un negocio). Donde no está, la curva de Sonora. */
export const TARIFAS_SUGERIR_CON_IA: readonly Tarifa[] = Object.freeze(['PDBT']);

/** ¿«Sugerir con IA» le pregunta a la IA? Solo un NEGOCIO con tarifa PDBT (lo medido). Una casa, nunca: ahí la curva acierta igual. */
export function sugerirUsaIA(o: { negocio: boolean; tarifa: string | null | undefined }): boolean {
  return o.negocio && !!o.tarifa && (TARIFAS_SUGERIR_CON_IA as readonly string[]).includes(o.tarifa);
}

/** Lo que la web le manda al servidor para que la IA sugiera (sin la ciudad: el servidor la toma de la cotización). Los periodos
 *  por estimar van en el orden de porEstimarDelPlan; la IA contesta un número por cada uno, en ese orden. */
export function cuerpoSugerir(plan: PlanCompletarAnio, o: { tarifa: Tarifa; facturacion: 'mensual' | 'bimestral'; negocio: boolean }): { cuerpo: Omit<EntradaSugerirConsumos, 'ciudad'>; porEstimar: PeriodoPorEstimar[] } {
  const porEstimar = porEstimarDelPlan(plan);
  return {
    porEstimar,
    cuerpo: {
      tarifa: o.tarifa,
      facturacion: o.facturacion,
      negocio: o.negocio,
      reales: plan.reales.map((r) => ({ inicio: r.inicio, fin: r.fin, kwh: r.kwh })),
      por_estimar: porEstimar.map((p) => ({ inicio: p.inicio, fin: p.fin, curva: p.curva })),
    },
  };
}

/** El motivo que queda en cada periodo que estimó la IA (y en el historial de la cotización): con qué lo estimó, para que nadie
 *  lo tome por un recibo. Empieza como el de la curva («Sugerir con IA:»). */
export const motivoSugeridoIA = (b: PlanCompletarAnio['base'], simulada: boolean, anual: number) =>
  `Sugerir con IA: estimado por la IA${simulada ? ' (SIMULADA)' : ''} con ${b.meses} ${b.meses === 1 ? 'periodo real' : 'periodos reales'} (${b.tramo}) y el clima de su ciudad (${numero(anual)} kWh al año).`;

/** El motivo con que la tabla guarda lo que sugirió la IA. Es lo único que deja a una tabla entera traer otros kWh con la marca
 *  (volver a sugerir): cualquier otro cambio de kWh quita la marca en el servidor (compartido: ediciones.ts). */
export const MOTIVO_SUGERIR_IA = '«Sugerir con IA»: kWh estimados por la IA';

/** hoy86 (verificación r2): el tope de un periodo en lo que se le manda a la IA. Un PDBT es de hasta 25 kW (unos 37 mil kWh en un
 *  bimestre si trabajara las 24 horas): 200 mil ya no es un negocio PDBT, es un cero de más. */
export const KWH_MAX_SUGERIR = 200_000;
/** Cuántas veces arriba del periodo real más alto puede venir la curva. En la tabla la curva sale de los reales: la diferencia entre
 *  el mes más alto y el más bajo del perfil es de unas 7 veces en una casa y menos en un negocio. */
export const VECES_CURVA_SOBRE_REAL = 20;

/** ¿Los números que manda la tabla se ven creíbles? null = sí; si no, el porqué. No se le pregunta a la IA (no se gasta): contra esa
 *  curva se comprueba lo que contesta (sugerenciaInvalida), y una curva inventada dejaría pasar cualquier número. */
export function cifrasRarasSugerir(c: Pick<EntradaSugerirConsumos, 'reales' | 'por_estimar'>): string | null {
  const alto = Math.max(...c.reales.map((r) => r.kwh));
  if (c.reales.some((r) => r.kwh > KWH_MAX_SUGERIR) || c.por_estimar.some((p) => p.curva > KWH_MAX_SUGERIR)) return `un periodo pasa de ${numero(KWH_MAX_SUGERIR)} kWh`;
  if (!(alto > 0)) return 'los periodos reales están en cero';
  if (c.por_estimar.some((p) => !(p.curva >= 1))) return 'la curva de Sonora no da consumo para un periodo';
  if (c.por_estimar.some((p) => p.curva > alto * VECES_CURVA_SOBRE_REAL)) return `la curva de Sonora da más de ${VECES_CURVA_SOBRE_REAL} veces el periodo real más alto`;
  return null;
}

/** El botón de Revisión que le quita a una casa lo que estimó la IA cuando era negocio (ver mensajeIAEnCasa). */
export const BOTON_CURVA_EN_CASA = 'Volver a llenar con la curva';

/** hoy86 (verificación de la 6.ª vuelta): una casa con meses que la IA estimó cuando era negocio. El PDF no sale así: la plantilla no
 *  acepta «ia» en residencial y «casas parecidas» sería falso de lo que estimó la IA. La misma frase en el rojo del motor (con cuáles) y
 *  en el PDF. No se le quita la marca ni se le cambian los kWh solo: el vendedor decide con qué se quedan. */
export function mensajeIAEnCasa(meses: { medidor: string; etiquetas: string; n: number } | null = null): string {
  const quien = meses ? `Medidor ${meses.medidor}: ${meses.etiquetas} ${meses.n === 1 ? 'lo estimó' : 'los estimó'}` : 'Los meses estimados los estimó';
  return `${quien} la IA para un negocio y esta cotización ya es de casa. Así el PDF no sale: en una casa ${meses?.n === 1 ? 'ese mes va' : 'esos meses van'} con la curva de Sonora. En Revisión aprieta «${BOTON_CURVA_EN_CASA}» o escribe sus kWh.`;
}

/** El principio del aviso AMARILLO cuando la IA no sirvió y se puso la curva (la web lo enseña; nunca se queda callada). */
export const FRASE_SUGERIR_FALLIDA = 'La IA no pudo sugerir los meses';

/** Lo que pide la web antes de rendirse con la IA y poner la curva (con aviso): la mediana medida fue de 2 a 8 segundos. */
export const ESPERA_SUGERIR_MS = 45_000;
